import { store } from 'store/store';
import { flushActivityQueue, isActivityQueueEnabled } from 'services/activityTracking/activityEventQueue';
import { closeAll } from 'services/activityTracking/focusPeriodTracker';
import { transitionOperatorState } from 'services/activityTracking/operatorStateTracker';
import {
    ACTIVITY_DOCUMENT_HIDDEN_GRACE_MS,
    ACTIVITY_END_REASON,
    ACTIVITY_WINDOW_BLUR_GRACE_MS,
    ACTIVITY_WINDOW_FOCUSED_ACTION,
    ACTIVITY_OPERATOR_STATE,
} from 'utils/activityTracking/constants';

/**
 * Page lifecycle triggers for the activity event stream (BEAST-13036).
 *
 * Focus is defined as "the transaction tab is active AND the document is visible", so a hidden
 * document ends the period as well as flushing. There is no `visibilitychange` listener anywhere
 * else in the repo, so this module owns the only one; page unload extends the existing central
 * `beforeunload` handler rather than adding a second unload listener.
 *
 * Both of these run on paths where a normal XHR may not survive, so they flush over `sendBeacon`.
 */

let registered = false;
let hiddenTimer = null;
let blurTimer = null;

function cancelHiddenTimer() {
    if (hiddenTimer !== null) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
    }
}

function cancelBlurTimer() {
    if (blurTimer !== null) {
        clearTimeout(blurTimer);
        blurTimer = null;
    }
}

/**
 * Ends the open periods because the document went away, and flushes over a beacon.
 *
 * Exported so the central `beforeunload` handler can call it directly for `page_unload`.
 */
export function endActivityPeriodsAndBeacon(endReason) {
    try {
        cancelHiddenTimer();
        closeAll({ endReason });
        transitionOperatorState(
            ACTIVITY_OPERATOR_STATE.OUTSIDE_APP,
            endReason === ACTIVITY_END_REASON.PAGE_UNLOAD ? 'page_unload' : 'document_hidden'
        );
        flushActivityQueue({ useBeacon: true });
    } catch (error) {
        console.warn('activityTracking: lifecycle flush failed.', error);
    }
}

function handleVisibilityChange() {
    try {
        if (typeof document === 'undefined') return;

        if (!document.hidden) {
            // Coming back does not reopen anything on its own: the tab saga and the field listener
            // open a new period when the agent next acts, and a new period is the correct output -
            // periods are never resumed.
            cancelHiddenTimer();
            cancelBlurTimer();
            return;
        }

        // The tab is going to the background; this path owns the close, so drop any pending
        // window-blur close so the two cannot double-fire.
        cancelBlurTimer();

        // OQ-5, decided: immediate, grace period zero. The constant exists so remaining gate 1
        // landing on a real grace period is this line plus the timer below, not a rewrite. The
        // analytical layer can always merge two adjacent periods separated by a short gap, but it
        // can never split one we coalesced, and the store is immutable.
        if (ACTIVITY_DOCUMENT_HIDDEN_GRACE_MS <= 0) {
            endActivityPeriodsAndBeacon(ACTIVITY_END_REASON.DOCUMENT_HIDDEN);
            return;
        }

        cancelHiddenTimer();
        hiddenTimer = setTimeout(() => {
            hiddenTimer = null;
            endActivityPeriodsAndBeacon(ACTIVITY_END_REASON.DOCUMENT_HIDDEN);
        }, ACTIVITY_DOCUMENT_HIDDEN_GRACE_MS);
    } catch (error) {
        console.warn('activityTracking: visibilitychange handling failed.', error);
    }
}

/**
 * Switching to another desktop app blurs the window but leaves the tab visible, so `visibilitychange`
 * never fires. End the open tab period after a short grace - a transient blur (clicking devtools and
 * back) is cancelled by the matching `focus` before the grace expires, so periods do not churn. The
 * field period is usually already closed by its own `focusout` with `relatedTarget === null`.
 */
function handleWindowBlur() {
    try {
        // The feature is off (flag disabled, or the queue stopped on a 401/403): do not arm a timer on
        // every window blur - the listeners are registered unconditionally at app start.
        if (!isActivityQueueEnabled()) return;
        // The document-hidden path owns the tab-backgrounded case; do not also arm a blur close for it.
        if (typeof document !== 'undefined' && document.hidden) return;

        cancelBlurTimer();
        blurTimer = setTimeout(() => {
            blurTimer = null;
            // A quick return within the grace re-focuses the window; if we are focused again (or the
            // tab has since been hidden and handled elsewhere), there is nothing to close.
            if (typeof document !== 'undefined' && (document.hasFocus() || document.hidden)) return;
            endActivityPeriodsAndBeacon(ACTIVITY_END_REASON.DOCUMENT_HIDDEN);
        }, ACTIVITY_WINDOW_BLUR_GRACE_MS);
    } catch (error) {
        console.warn('activityTracking: window blur handling failed.', error);
    }
}

/**
 * The window regained OS focus. Cancel any pending blur close, and ask the saga to reopen the tab
 * period for the current active transaction - the DOM listener has no store access to derive it, and
 * `switchTo` there is a no-op when that period is still open (a blur that never closed anything). The
 * field period self-heals: the browser refocuses the input, which fires `focusin` -> `openFieldPeriod`.
 */
function handleWindowFocus() {
    try {
        // Always clear a pending blur close (harmless if none), but only signal a reopen when the
        // feature is actually running - the listeners are registered unconditionally at app start.
        cancelBlurTimer();
        if (!isActivityQueueEnabled()) return;
        store.dispatch({ type: ACTIVITY_WINDOW_FOCUSED_ACTION });
    } catch (error) {
        console.warn('activityTracking: window focus handling failed.', error);
    }
}

/** Idempotent, so a re-render or a remount cannot install a second listener. */
export function registerActivityLifecycleListeners() {
    if (registered || typeof document === 'undefined') return () => {};

    registered = true;
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleWindowBlur);
        window.removeEventListener('focus', handleWindowFocus);
        cancelHiddenTimer();
        cancelBlurTimer();
        registered = false;
    };
}

/** Test seam only. */
export function resetActivityLifecycle() {
    cancelHiddenTimer();
    cancelBlurTimer();
    registered = false;
}
